import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import { Botao, PageHeader } from '../../../ui';
import StepperAdesao from '../../../components/adesao/StepperAdesao';
import FormularioAssociado, {
  DadosAssociado,
} from '../../../components/associado/FormularioAssociado';

import { updateAssociadoData } from '../../../store/associadoSlice';
import { RootState, store } from '../../../store/store';
import { salvarEtapaRascunho } from '../../../utils/adesaoFluxo';
import { listarRascunhos } from '../../../api/rascunho-adesao/rascunhoAdesaoApi';

import { CARRO } from '../../carro/routes';
import { ADESOES } from '../../adesoes/routes';

/**
 * `/associado` — **etapa 1 da adesão** (reescrita na at-58, frente C).
 *
 * A tela é puramente local: o consultor preenche, os dados vão para o estado do
 * fluxo e para o rascunho, e a etapa 2 abre. **Nada é enviado ao servidor aqui**
 * além do rascunho — o `POST /associado/cadastrar` acontece na etapa 3
 * (`/documento`).
 *
 * ## O que a reescrita mudou de COMPORTAMENTO (spec 03)
 *
 *   1. **A tela abre PREENCHIDA** quando há dados no fluxo (§7.2). Antes nascia
 *      sempre vazia: quem voltasse de `/carro` e salvasse **sobrescrevia o
 *      titular com o formulário em branco**.
 *   2. **Fim dos 3 segundos de espera** antes de navegar (§4: *"não é requisito —
 *      é um defeito de experiência"*). O toast sobrevive à navegação, porque o
 *      `ToastContainer` é único e vive no `index.tsx` (at-19).
 *   3. **Erro aparece junto do campo**, com `role="alert"` (§4), e há validação
 *      de verdade — CPF com dígito verificador, e-mail, data não futura, CEP.
 *      Antes a única regra era "não vazio", e o erro só surgia na etapa 3.
 *   4. **Sem `FullScreenLoader`** (§7.1): não há chamada síncrona a esperar no
 *      "Salvar" — o rascunho é fire-and-forget. O botão vira "Salvando..." e
 *      desabilita, sem cobrir a tela.
 *   5. **Stepper** dizendo "Etapa 1 de 6" — o fluxo não informava onde se estava.
 *   6. **"Sexo: Outro" saiu** (D8): o backend só aceita `MASCULINO`/`FEMININO` e
 *      recusava com 400 **na etapa 3**, três telas depois.
 *   7. **CPF já cadastrado avisa aqui** (D3), **CEP preenche o endereço** (D4) e
 *      **RG/Número aceitam formato livre** (D5).
 *
 * ## O que NÃO mudou, de propósito (spec 03 §9)
 *
 * As chaves gravadas no estado, o contrato do rascunho (fire-and-forget, falha
 * nunca bloqueia, 409 tratado), a regional obrigatória vinda da lista do próprio
 * consultor, e a tela nascer limpa numa adesão nova.
 */
const AssociadoPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const voltar = useVoltar();

  const rascunhoId = useSelector((state: RootState) => state.associado.rascunhoId);

  /**
   * Preenchimento inicial a partir do estado do fluxo (spec 03 §7.2).
   *
   * ⚠️ Lido **uma vez**, no primeiro render: é o `valoresIniciais` do formulário,
   * que não re-sincroniza depois (ver o comentário lá). Numa adesão nova o slice
   * já foi limpo por `iniciarNovaAdesao` — pelo botão "Nova Adesão +" de
   * `/adesoes` ou pelo menu —, então isto resulta em formulário vazio.
   */
  const [valoresIniciais] = useState(() => {
    const associado = store.getState().associado;
    if (!associado?.cpfCnpj && !associado?.nomeCompleto) return null;

    return associado as unknown as Partial<DadosAssociado>;
  });

  /** Quantas adesões o consultor deixou pela metade (at-38) — só aviso. */
  const [rascunhosEmAndamento, setRascunhosEmAndamento] = useState(0);

  /**
   * Faixa de aviso: quando o consultor abre uma adesão NOVA (sem rascunho em
   * curso) mas tem trabalho parado, dizer isso antes de ele digitar tudo de novo.
   *
   * ⚠️ Falha é **silenciosa** de propósito (spec 03 §2): é informação acessória e
   * nada aqui pode atrapalhar o cadastro.
   */
  useEffect(() => {
    if (rascunhoId) return undefined;

    let vivo = true;
    listarRascunhos({ limit: 1 })
      .then((r) => {
        if (vivo) setRascunhosEmAndamento(r.totais?.emAndamento ?? 0);
      })
      .catch(() => {
        /* aviso acessório */
      });

    return () => {
      vivo = false;
    };
  }, [rascunhoId]);

  const salvar = (dados: DadosAssociado) => {
    dispatch(updateAssociadoData(dados));

    /*
     * Rascunho (at-38): a etapa concluída é gravada no banco logo após o
     * dispatch — `store.getState()` porque o `useSelector` desta renderização
     * ainda não conhece o que acabou de entrar no slice. Sem `await`: rede lenta
     * não pode atrasar a navegação, e falha aqui NUNCA interrompe o cadastro.
     */
    void salvarEtapaRascunho(dispatch, () => store.getState(), 'CARRO');

    // ⚠️ Sem `setTimeout`. O toast sobrevive à navegação (container único).
    navigate(CARRO());
  };

  return (
    <ShellSaac
      cabecalho={
        <PageHeader titulo="Associado" sobretitulo="Nova Adesão" aoVoltar={voltar} />
      }
    >
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-3xl md:tw-p-6">
          <div className="tw-bg-white md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            <div className="tw-border-b tw-border-superficie-borda">
              <StepperAdesao etapa={1} fluxo={"ADESAO"} />
            </div>

            <div className="tw-p-4 md:tw-p-6">
              <FormularioAssociado
                rotuloAcao="Continuar — Veículo"
                valoresIniciais={valoresIniciais}
                comChecagemCpf
                aoSalvar={salvar}
                aviso={
                  /* Não bloqueia nada: o consultor pode ignorar e seguir. */
                  !rascunhoId && rascunhosEmAndamento > 0 ? (
                    <div className="tw-flex tw-flex-col tw-gap-2 tw-rounded-xl tw-border tw-border-amber-warn/30 tw-bg-amber-bg tw-px-4 tw-py-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
                      <p className="tw-m-0 tw-text-sm tw-text-amber-deep">
                        Você tem <strong>{rascunhosEmAndamento}</strong>{' '}
                        {rascunhosEmAndamento === 1 ? 'adesão' : 'adesões'} em andamento.
                      </p>
                      <Botao
                        variante="secundario"
                        tamanho="sm"
                        onClick={() => navigate(ADESOES(), { state: { aba: 'rascunhos' } })}
                        className="sm:tw-shrink-0"
                      >
                        Ver rascunhos
                      </Botao>
                    </div>
                  ) : null
                }
              />
            </div>
          </div>
        </div>
      </div>
    </ShellSaac>
  );
};

export default AssociadoPage;
